import {useSelector} from 'react-redux';
import type {RootState} from '@/store';
import {aiPromptBoxActions} from '@Components/ai-prompt-box/ai-prompt-box.reducer';
import type {AiPromptBox, SelectedContextualMenuItems} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {useAppDispatch} from '@/hooks';
import type {ContextualMenuGroup} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu/ai-prompt-box-contextual-menu.types';
import {getDefaultAiPromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box-library';
import {useCallback, useEffect, useMemo, useRef} from 'react';
import {useLatest} from 'ahooks';
import type {DeepPartial} from '@/global';
import type {LoadingAttachment, PromptAttachment, UserImageAttachment} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {createLoadingAttachments} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import {isPromptBoxProcessing} from '@Components/ai-prompt-box/ai-prompt-box-library';
import {consumePendingAiPromptBoxPreFill} from '@Components/ai-prompt-box/libraries/ai-prompt-box-pre-fill.library';
import {PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {submitPendingAiChatPromptIfReady} from '@Libraries/ai-chat.library';

export interface AiPromptBoxActions {
  update: (options: DeepPartial<AiPromptBox>) => void;
  setActiveIfNotProcessing: VoidFunction;
  addAttachments: (attachments: PromptAttachment[], loadingAttachments?: LoadingAttachment[]) => void;
  addDesignTemplateAttachments: (attachments: PromptAttachment[]) => void;
  addLoadingAttachments: (count: number) => string[];
  removeLoadingAttachments: (loadingIds: string[]) => void;
  removeImageAttachments: (hashedFilenames: string[]) => void;
  addPendingImageAttachments: (attachments: UserImageAttachment[]) => void;
  resolvePendingAttachment: (localId: string, resolved: UserImageAttachment) => void;
  markPendingAttachmentFailed: (localId: string) => void;
  markPendingAttachmentUploading: (localId: string) => void;
  removePendingAttachment: (localId: string) => void;
}

export interface UseAiPromptBox {
  id: string;
  state: AiPromptBox;
  actions: AiPromptBoxActions;
  selectedContextualMenuItems: SelectedContextualMenuItems;
  isInitialized: boolean;
}

const getSelectedContextualMenuItems = (groups?: ContextualMenuGroup[]): SelectedContextualMenuItems => {
  if (!groups) {
    return {};
  }

  const result: SelectedContextualMenuItems = {};

  groups.forEach((group) => {
    const selectedItems = group.items.filter((item) => item.active).map((item) => item.value);

    if (selectedItems.length > 0) {
      result[group.key] = selectedItems;
    }
  });

  return result;
};

const buildInitialPromptBoxState = (initialConfig: Partial<AiPromptBox> | undefined, isMobile: boolean): AiPromptBox => {
  const defaultState = getDefaultAiPromptBoxState(isMobile);
  return {
    ...defaultState,
    ...(initialConfig ?? {}),
    transcription: {
      ...defaultState.transcription,
      ...(initialConfig?.transcription ?? {}),
    },
  };
};

// A prompt pre-fill queued before this box mounted (see ai-prompt-box-pre-fill.library) fills an empty box and wakes it from IDLE.
// The pre-fill is consumed either way so it cannot leak into an unrelated later mount.
const applyPendingPromptPreFill = (id: string, state: AiPromptBox): AiPromptBox => {
  const pendingPreFill = consumePendingAiPromptBoxPreFill(id);
  if (pendingPreFill === undefined || state.prompt.trim() !== '') {
    return state;
  }
  return {
    ...state,
    prompt: pendingPreFill,
    lastPreFilledPrompt: pendingPreFill,
    promptBoxState: state.promptBoxState === PromptBoxState.IDLE ? PromptBoxState.ACTIVE : state.promptBoxState,
  };
};

export const useAiPromptBox = (id: string, initialConfig?: Partial<AiPromptBox>, isMobile = false): UseAiPromptBox => {
  const aiPromptBoxReduxState = useSelector((state: RootState) => state.aiPromptBox);
  const aiPromptBoxState = aiPromptBoxReduxState[id] ?? undefined;
  const dispatch = useAppDispatch();
  const initializedIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    void submitPendingAiChatPromptIfReady();
  }, []);

  const update = useCallback(
    (options: DeepPartial<AiPromptBox>): void => {
      dispatch(
        aiPromptBoxActions.updateAiPromptBox({
          id,
          value: options,
        })
      );
    },
    [dispatch, id]
  );

  const addAttachments = useCallback(
    (attachments: PromptAttachment[], loadingAttachments?: LoadingAttachment[]) => {
      dispatch(
        aiPromptBoxActions.addAttachments({
          id,
          attachments,
          loadingAttachments,
        })
      );
    },
    [dispatch, id]
  );

  const addDesignTemplateAttachments = useCallback(
    (attachments: PromptAttachment[]): void => {
      dispatch(
        aiPromptBoxActions.addAttachments({
          id,
          attachments,
          prefillPromptIfEmpty: window.i18next.t('pmwjs_use_this_design'),
        })
      );
    },
    [dispatch, id]
  );

  const addLoadingAttachments = useCallback(
    (count: number): string[] => {
      const {loadingIds, newLoadingAttachments} = createLoadingAttachments(count);
      dispatch(
        aiPromptBoxActions.addLoadingAttachments({
          id,
          loadingAttachments: newLoadingAttachments,
        })
      );
      return loadingIds;
    },
    [dispatch, id]
  );

  const removeLoadingAttachments = useCallback(
    (loadingIds: string[]): void => {
      dispatch(
        aiPromptBoxActions.removeLoadingAttachments({
          id,
          loadingIds,
        })
      );
    },
    [dispatch, id]
  );

  const removeImageAttachments = useCallback(
    (hashedFilenames: string[]): void => {
      dispatch(
        aiPromptBoxActions.removeImageAttachments({
          id,
          hashedFilenames,
        })
      );
    },
    [dispatch, id]
  );

  const addPendingImageAttachments = useCallback(
    (attachments: UserImageAttachment[]): void => {
      dispatch(
        aiPromptBoxActions.addPendingImageAttachments({
          id,
          attachments,
        })
      );
    },
    [dispatch, id]
  );

  const resolvePendingAttachment = useCallback(
    (localId: string, resolved: UserImageAttachment): void => {
      dispatch(
        aiPromptBoxActions.resolvePendingAttachment({
          id,
          localId,
          resolved,
        })
      );
    },
    [dispatch, id]
  );

  const markPendingAttachmentFailed = useCallback(
    (localId: string): void => {
      dispatch(
        aiPromptBoxActions.markPendingAttachmentFailed({
          id,
          localId,
        })
      );
    },
    [dispatch, id]
  );

  const markPendingAttachmentUploading = useCallback(
    (localId: string): void => {
      dispatch(
        aiPromptBoxActions.markPendingAttachmentUploading({
          id,
          localId,
        })
      );
    },
    [dispatch, id]
  );

  const removePendingAttachment = useCallback(
    (localId: string): void => {
      dispatch(
        aiPromptBoxActions.removePendingAttachment({
          id,
          localId,
        })
      );
    },
    [dispatch, id]
  );

  const promptBoxStateRef = useLatest(aiPromptBoxState?.promptBoxState);

  const setActiveIfNotProcessing = useCallback((): void => {
    if (!isPromptBoxProcessing(promptBoxStateRef.current ?? PromptBoxState.IDLE)) {
      update({promptBoxState: PromptBoxState.ACTIVE});
    }
  }, [update, promptBoxStateRef]);

  const actions = useMemo(
    () => ({
      update,
      setActiveIfNotProcessing,
      addAttachments,
      addDesignTemplateAttachments,
      addLoadingAttachments,
      removeLoadingAttachments,
      removeImageAttachments,
      addPendingImageAttachments,
      resolvePendingAttachment,
      markPendingAttachmentFailed,
      markPendingAttachmentUploading,
      removePendingAttachment,
    }),
    [update, setActiveIfNotProcessing, addAttachments, addDesignTemplateAttachments, addLoadingAttachments, removeLoadingAttachments, removeImageAttachments, addPendingImageAttachments, resolvePendingAttachment, markPendingAttachmentFailed, markPendingAttachmentUploading, removePendingAttachment]
  );

  if (!aiPromptBoxState && !initializedIds.current.has(id) && initialConfig !== undefined) {
    dispatch(
      aiPromptBoxActions.initializeAiPromptBox({
        id,
        value: applyPendingPromptPreFill(id, buildInitialPromptBoxState(initialConfig, isMobile)),
      })
    );
    initializedIds.current.add(id);
  }

  const currentState = aiPromptBoxState ?? buildInitialPromptBoxState(initialConfig, isMobile);

  const selectedContextualMenuItems = useMemo(() => getSelectedContextualMenuItems(currentState.contextualMenu), [currentState.contextualMenu]);

  return {
    id,
    state: currentState,
    actions,
    selectedContextualMenuItems,
    isInitialized: !!aiPromptBoxState,
  };
};
